import React, { useMemo, useState } from 'react';
import { SunIcon, MoonIcon, HouseIcon, AlertCircleIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
import { MOCK_LOANS, LOAN_STAGES, AlertType, ALERT_TYPES, Loan, LoanStage } from '../data/mockData';
import { LoanRecordPreview } from './LoanRecordPreview';
import { PipelineFilters } from './PipelineFilters';
import { StageSection } from './StageSection';
export const LoanPipeline: React.FC = () => {
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedAlerts, setSelectedAlerts] = useState<AlertType[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [darkMode, setDarkMode] = useState<boolean>(false);
  // Initialize all stages as expanded
  const [expandedStages, setExpandedStages] = useState<Record<LoanStage, boolean>>(() => {
    const initial: Partial<Record<LoanStage, boolean>> = {};
    LOAN_STAGES.forEach(stage => {
      initial[stage] = true;
    });
    return initial as Record<LoanStage, boolean>;
  });
  const handleClearFilters = () => {
    setSelectedDivisions([]);
    setSelectedTeams([]);
    setSelectedAlerts([]);
    setSearchQuery('');
  };
  const toggleStage = (stage: LoanStage) => {
    setExpandedStages(prev => ({
      ...prev,
      [stage]: !prev[stage]
    }));
  };
  const expandAll = () => {
    const allExpanded: Partial<Record<LoanStage, boolean>> = {};
    LOAN_STAGES.forEach(stage => {
      allExpanded[stage] = true;
    });
    setExpandedStages(allExpanded as Record<LoanStage, boolean>);
  };
  const collapseAll = () => {
    const allCollapsed: Partial<Record<LoanStage, boolean>> = {};
    LOAN_STAGES.forEach(stage => {
      allCollapsed[stage] = false;
    });
    setExpandedStages(allCollapsed as Record<LoanStage, boolean>);
  };
  // Loans filtered by everything EXCEPT alerts (for KPI counts)
  const loansBeforeAlertFilter = useMemo(() => {
    return MOCK_LOANS.filter(loan => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesSearch = loan.borrowerName.toLowerCase().includes(q) || loan.loanNumber.toLowerCase().includes(q) || loan.loanOfficer.toLowerCase().includes(q) || loan.propertyAddress.toLowerCase().includes(q) || loan.teamLead.toLowerCase().includes(q) || loan.divisionLead.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }
      if (selectedDivisions.length > 0 && !selectedDivisions.includes(loan.divisionLead)) {
        return false;
      }
      if (selectedTeams.length > 0 && !selectedTeams.includes(loan.teamLead)) {
        return false;
      }
      return true;
    });
  }, [selectedDivisions, selectedTeams, searchQuery]);
  const filteredLoans = useMemo(() => {
    return loansBeforeAlertFilter.filter(loan => {
      if (selectedAlerts.length > 0) {
        const hasMatchingAlert = loan.alerts.some(alert => selectedAlerts.includes(alert));
        if (!hasMatchingAlert) return false;
      }
      return true;
    });
  }, [loansBeforeAlertFilter, selectedAlerts]);
  // Compute alert counts from loansBeforeAlertFilter
  const alertCounts = useMemo(() => {
    const counts: Record<AlertType, number> = {} as Record<AlertType, number>;
    ALERT_TYPES.forEach(alert => {
      counts[alert] = loansBeforeAlertFilter.filter(loan => loan.alerts.includes(alert)).length;
    });
    return counts;
  }, [loansBeforeAlertFilter]);
  const totalPipelineValue = useMemo(() => {
    return filteredLoans.reduce((sum, loan) => sum + loan.amount, 0);
  }, [filteredLoans]);
  const formattedPipelineValue = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(totalPipelineValue);
  const getAlertCardStyle = (alert: AlertType) => {
    switch (alert) {
      case 'Rate Lock Expiring':
      case 'Missing Documents':
      case 'Closing Date Approaching':
        return {
          color: 'red',
          icon: <AlertCircleIcon className="w-4 h-4" data-id="element-24" data-mp="3k82byr4ts6ej" />
        };
      case 'AWC No Appraisal Back':
      case 'Disclosures Out Not Signed':
      case 'Disclosures Signed Not Submitted':
        return {
          color: 'orange',
          icon: <AlertTriangleIcon className="w-4 h-4" data-id="element-25" data-mp="3t8qg8u4f2se8" />
        };
      case 'AWC Appraisal Back':
        return {
          color: 'green',
          icon: <CheckCircleIcon className="w-4 h-4" data-id="element-26" data-mp="6tpc8jnabx8t" />
        };
      default:
        return {
          color: 'gray',
          icon: null
        };
    }
  };
  const allExpanded = Object.values(expandedStages).every(Boolean);
  return <div className={`flex flex-col h-screen overflow-hidden ${darkMode ? 'dark bg-gray-900' : 'bg-[#F4F6FA]'}`} data-id="element-27" data-mp="gsmy1etbtco4">
      {/* eMortgage Funding brand bar */}
      <header className="bg-[#0B1856] border-b-4 border-[#FF8000] px-5 sm:px-6 py-3.5 flex items-center justify-between flex-none transition-colors duration-200" data-id="element-28" data-mp="25azj9hogxy5m">
        <div className="flex items-center gap-3" data-id="element-29" data-mp="21bqoc9hozu8h">
          <div className="w-10 h-10 bg-[#FF8000] flex items-center justify-center shadow-sm" aria-hidden="true" data-id="element-30" data-mp="1w9rk6xe4otah">
            <HouseIcon className="h-6 w-6 text-white stroke-[2.5]" data-id="element-31" data-mp="1rchudgmtufm4" />
          </div>
          <div className="leading-none" data-id="element-32" data-mp="2ozhz38nl3z7n">
            <p className="font-bold text-white text-lg tracking-[-0.04em]" data-id="element-33" data-mp="2oyf7eyiftsqo">eMortgage <span className="text-[#FF8000]" data-id="element-34" data-mp="2k16t590eu8up">Funding</span></p>
            <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#A8DDF4]" data-id="element-35" data-mp="2f95h9o75mk57">Operations workspace</p>
          </div>
        </div>
        <div className="flex items-center gap-3 sm:gap-4" data-id="element-36" data-mp="2a9stn18gl91k">
          {/* Pipeline Value + Loan Count */}
          <div className="hidden md:flex items-center gap-3 text-right" data-id="element-37" data-mp="37r10xc5vh783">
            <div data-id="element-38" data-mp="32xyednziiy1e">
              <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-[#A8DDF4]" data-id="element-39" data-mp="32xeva3y4m3ls">Pipeline value</p>
              <p className="text-lg leading-tight font-bold text-white" data-id="element-40" data-mp="113k6t5yovf4e">{formattedPipelineValue}</p>
            </div>
            <div className="h-8 w-px bg-white/20" data-id="element-41" data-mp="rbnxszv2e4le"></div>
            <div className="bg-white/10 border border-white/15 text-white text-xs font-semibold px-3 py-2 rounded-sm whitespace-nowrap" data-id="element-42" data-mp="rauvcb352192">
              {filteredLoans.length} active loans
            </div>
          </div>

          {/* Dark Mode Toggle */}
          <button onClick={() => setDarkMode(!darkMode)} className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#009EDB] focus:ring-offset-2 focus:ring-offset-[#0B1856] ${darkMode ? 'bg-[#009EDB]' : 'bg-white/20'}`} aria-label="Toggle dark mode" data-id="element-43" data-mp="mcbdomvkvbmi">
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-sm transition-transform duration-300 flex items-center justify-center ${darkMode ? 'translate-x-7' : 'translate-x-1'}`} data-id="element-44" data-mp="27pw7vuynelg5">
              {darkMode ? <MoonIcon className="h-3.5 w-3.5 text-[#0B1856]" data-id="element-45" data-mp="2cwkvtubhggfr" /> : <SunIcon className="h-3.5 w-3.5 text-[#FF8000]" data-id="element-46" data-mp="2qmy09mp61d0c" />}
            </span>
          </button>

          <div className="w-8 h-8 rounded-full bg-[#009EDB] text-white flex items-center justify-center font-bold text-xs ring-2 ring-white/25" aria-label="Signed in as JD" data-id="element-47" data-mp="2lu4z18swomt0">
            JD
          </div>
        </div>
      </header>

      {/* Filters */}
      <PipelineFilters selectedDivisions={selectedDivisions} setSelectedDivisions={setSelectedDivisions} selectedTeams={selectedTeams} setSelectedTeams={setSelectedTeams} searchQuery={searchQuery} setSearchQuery={setSearchQuery} onClearFilters={handleClearFilters} data-id="element-48" data-mp="39ladbjj77717" />

      {/* Pipeline Board (List View) */}
      <main className="flex-1 overflow-y-auto p-5 sm:p-6 custom-scrollbar" data-id="element-49" data-mp="1zqcad96v96bn">
        <div className="w-full max-w-[1600px] mx-auto" data-id="element-50" data-mp="1zo8b54jwwvhp">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5" data-id="element-51" data-mp="24odtfu4al5n4">
            <div data-id="element-52" data-mp="29fvvkli1xc9o">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#009EDB]" data-id="element-53" data-mp="29hfb29vvvjae">Loan management</p>
              <h1 className="mt-1 text-xl font-bold tracking-[-0.03em] text-[#0B1856] dark:text-white" data-id="element-54" data-mp="2dgxx58zfy0et">Pipeline overview</h1>
              <p className="mt-1 text-sm text-[#0B1856]/65 dark:text-gray-400" data-id="element-55" data-mp="1gtta79e1isr8">Monitor active files, manage priority conditions, and keep every borrower moving forward.</p>
            </div>
            <button onClick={allExpanded ? collapseAll : expandAll} className="hidden sm:inline-flex items-center justify-center text-xs font-bold uppercase tracking-[0.1em] text-[#0B1856] dark:text-white hover:text-[#009EDB] transition-colors whitespace-nowrap" data-id="element-56" data-mp="1pu9c4ef4xrtz">
              {allExpanded ? 'Collapse all stages' : 'Expand all stages'}
            </button>
          </div>

          {/* Alert KPI Cards */}
          <div className="flex items-start justify-between mb-4" data-id="element-57" data-mp="3bayyot0gksvl">
            <div className="flex flex-wrap gap-2.5" data-id="element-58" data-mp="36kihqyxfip4h">
              {ALERT_TYPES.map(alert => {
              const isActive = selectedAlerts.includes(alert);
              const {
                color,
                icon
              } = getAlertCardStyle(alert);
              const count = alertCounts[alert];
              const colorClasses = {
                red: {
                  active: 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20',
                  inactive: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-red-300 dark:hover:border-red-700',
                  icon: 'text-red-500 dark:text-red-400',
                  count: 'text-red-600 dark:text-red-400'
                },
                orange: {
                  active: 'border-orange-400 dark:border-orange-500 bg-orange-50 dark:bg-orange-900/20',
                  inactive: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-orange-300 dark:hover:border-orange-700',
                  icon: 'text-orange-500 dark:text-orange-400',
                  count: 'text-orange-600 dark:text-orange-400'
                },
                green: {
                  active: 'border-green-400 dark:border-green-500 bg-green-50 dark:bg-green-900/20',
                  inactive: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-green-300 dark:hover:border-green-700',
                  icon: 'text-green-500 dark:text-green-400',
                  count: 'text-green-600 dark:text-green-400'
                },
                gray: {
                  active: 'border-gray-400 dark:border-gray-500 bg-gray-50 dark:bg-gray-700',
                  inactive: 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300',
                  icon: 'text-gray-500 dark:text-gray-400',
                  count: 'text-gray-600 dark:text-gray-400'
                }
              };
              const classes = colorClasses[color as keyof typeof colorClasses];
              return <button key={alert} onClick={() => {
                if (isActive) {
                  setSelectedAlerts(selectedAlerts.filter(a => a !== alert));
                } else {
                  setSelectedAlerts([...selectedAlerts, alert]);
                }
              }} className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-sm border shadow-sm transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#009EDB] ${isActive ? classes.active : classes.inactive}`} data-id="element-59" data-mp="b2901xlsj4sc">
                    <span className={classes.icon} data-id="element-60" data-mp="se7hebqn1z7z">{icon}</span>
                    <div className="flex flex-col items-start" data-id="element-61" data-mp="nm5y45v1z88g">
                      <span className={`text-xl font-bold leading-none ${classes.count}`} data-id="element-62" data-mp="jlcbrgx9e1ho">
                        {count}
                      </span>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight mt-0.5 whitespace-nowrap" data-id="element-63" data-mp="9n6b12n6v1ee">
                        {alert}
                      </span>
                    </div>
                  </button>;
            })}
            </div>

          </div>

          {/* Active alert filter indicator */}
          {selectedAlerts.length > 0 && <div className="flex items-center gap-2 mb-3" data-id="element-64" data-mp="1yzrykn6d4w9a">
              <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-65" data-mp="1v1u8li4rq64u">
                Filtering by {selectedAlerts.length} alert
                {selectedAlerts.length > 1 ? 's' : ''}
              </span>
              <button onClick={() => setSelectedAlerts([])} className="text-xs text-[#009EDB] hover:text-[#0B1856] font-medium transition-colors" data-id="element-66" data-mp="3hvo2j18tzfes">
                Clear alert filters
              </button>
            </div>}

          {/* Stage Sections */}
          <div className="flex flex-col gap-2" data-id="element-67" data-mp="2u1cyd9235p9t">
            {LOAN_STAGES.map(stage => {
            const stageLoans = filteredLoans.filter(loan => loan.stage === stage);
            return <StageSection key={stage} stage={stage} loans={stageLoans} isExpanded={expandedStages[stage]} onToggle={() => toggleStage(stage)} onSelectLoan={setSelectedLoan} data-id="element-68" data-mp="37zkooy4m9sc0" />;
          })}
          </div>
        </div>
      </main>

      {selectedLoan && <LoanRecordPreview loan={selectedLoan} onClose={() => setSelectedLoan(null)} data-id="element-69" data-mp="gt3l36njhbwl" />}
    </div>;
};